import {
  navigateWorkplaceReminderDeepLink,
  useWorkplaceReminders,
  type WorkplaceReminderNavigationHandlers,
} from "./useWorkplaceReminders";
import { WorkplaceRemindersList } from "./WorkplaceRemindersList";
import type { ReassignMemberOption } from "./WorkplaceReminderHygieneActions";

export type WorkplaceRemindersSectionProps = {
  surface: "bell" | "checklist" | "lead";
  leadId?: string | null;
  orgManagerNudge?: { title: string; body: string; sent_at: string } | null;
  compact?: boolean;
  navigation?: WorkplaceReminderNavigationHandlers;
  reassignMembers?: ReassignMemberOption[];
  /** Called after dismiss / ack / resolve so the bell summary can refresh. */
  onChanged?: () => void;
};

export function WorkplaceRemindersSection({
  surface,
  leadId,
  orgManagerNudge,
  compact,
  navigation,
  reassignMembers,
  onChanged,
}: WorkplaceRemindersSectionProps) {
  const { reminders, loading, error, dismiss, acknowledge, resolve, reassign, canReassign, reassignMembers: hookMembers } =
    useWorkplaceReminders({
      surface,
      leadId,
      orgManagerNudge,
      reassignMembers,
    });

  if (loading && reminders.length === 0) {
    return <p className="text-[11px] text-gray-500">Loading reminders…</p>;
  }
  if (error) {
    return <p className="text-[11px] text-red-600 dark:text-red-300">{error}</p>;
  }
  if (reminders.length === 0) return null;

  return (
    <WorkplaceRemindersList
      reminders={reminders}
      compact={compact}
      canReassign={canReassign}
      reassignMembers={hookMembers}
      onDismiss={(id) => {
        void dismiss(id).then(() => onChanged?.());
      }}
      onAcknowledge={(id) => {
        void acknowledge(id).then(() => onChanged?.());
      }}
      onResolve={(id, reason) => {
        void resolve(id, reason).then(() => onChanged?.());
      }}
      onReassign={(id, assignee, reason) => {
        void reassign(id, assignee, reason).then(() => onChanged?.());
      }}
      onNavigate={navigation ? (link) => navigateWorkplaceReminderDeepLink(link, navigation) : undefined}
    />
  );
}
